import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { NS, ORG_SETTINGS_KEYS } from '../../../i18n/constants/keys';
import { AdvancedSettings } from './Containers';
import { GeneralSettings } from './SettingsSections/GeneralSettings';
import OrganizationHierarchy from './SettingsSections/OrganizationHierarchy';
import { Subscription } from './SettingsSections/Subscription';
import { SecuritySettings } from './SettingsSections/SecuritySettings';

export const OrganizationSettings = () => {
  const { t } = useTranslation(NS.organizationSettings);
  const [activeTab, setActiveTab] = useState<number>(1); // Default to General Settings
  const tabTitles = [
    { labelKey: ORG_SETTINGS_KEYS.tabs.hierarchy, id: 0 },
    { labelKey: ORG_SETTINGS_KEYS.tabs.general, id: 1 },
    { labelKey: ORG_SETTINGS_KEYS.tabs.subscription, id: 2 },
    { label: 'Security', id: 3 },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return <OrganizationHierarchy />;
      case 1:
        return <GeneralSettings />;
      case 2:
        return <Subscription />;
      case 3:
        return <SecuritySettings />;
      case 4:
        return <AdvancedSettings />;
      default:
        return <GeneralSettings />;
    }
  };

  return (
    <div className='w-full h-full flex flex-col gap-6 max-w-7xl mx-auto'>
      {/* Apple-styled Breadcrumbs */}
      <nav
        className='flex w-full items-center py-2.5 text-xs text-slate-500 font-medium'
        aria-label='Breadcrumb'
      >
        <ol className='inline-flex items-center space-x-2'>
          <li>
            <Link
              to={'#'}
              className='text-slate-400 hover:text-slate-700 transition-colors'
            >
              {t(ORG_SETTINGS_KEYS.breadcrumbs.admin)}
            </Link>
          </li>
          <li>
            <span className='text-slate-300 font-normal'>/</span>
          </li>
          <li>
            <span className='text-slate-800 font-bold tracking-tight'>
              {t(ORG_SETTINGS_KEYS.breadcrumbs.organizationSettings)}
            </span>
          </li>
        </ol>
      </nav>

      {/* Apple Segmented Tab Control */}
      <div className='flex items-center justify-between pb-1 border-b border-slate-200/80'>
        <div className='flex items-center gap-1.5 p-1 bg-slate-100/90 backdrop-blur-md rounded-2xl border border-slate-200/70 shadow-inner'>
          {tabTitles.map(({ labelKey, label, id }: { labelKey?: string; label?: string; id: number }) => {
            const isActive = activeTab === id;
            return (
              <button
                key={labelKey || label}
                onClick={() => setActiveTab(id)}
                className={`relative px-4 py-2 text-xs font-bold rounded-xl transition-colors duration-200 select-none z-10 ${
                  isActive
                    ? 'text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {isActive && (
                  <motion.div
                    layoutId='active-settings-tab-pill'
                    transition={{
                      type: 'spring',
                      damping: 26,
                      stiffness: 350,
                    }}
                    className='absolute inset-0 bg-white rounded-xl shadow-sm border border-slate-200/60 -z-10'
                  />
                )}
                <span>{labelKey ? t(labelKey) : label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Contents with Smooth Fade & Motion */}
      <AnimatePresence exitBeforeEnter>
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className='w-full'
        >
          {renderTabContent()}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
